"use client"

import type React from "react"
import { useCallback, useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { cn } from "@/lib/utils"
import {
  categories,
  getToolById,
  tools,
  type Tool,
  type ToolCategory,
} from "@/lib/tools-registry"
import { openCommandPalette } from "@/lib/command-palette-events"
import { useTools } from "./tools-context"
import { ThemeToggle } from "./theme-toggle"
import {
  ChevronRight,
  Clock,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  Sparkles,
  Spade,
  Star,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Logo } from "./logo"

const SIDEBAR_COLLAPSED_KEY = "wkit-sidebar-collapsed"
const SIDEBAR_GROUPS_KEY = "wkit-sidebar-open-groups"
const MAX_PINNED_TOOLS = 5
const MAX_RECENT_TOOLS = 4

function getToolsByCategory(category: ToolCategory): Tool[] {
  return tools.filter((tool) => tool.category === category)
}

function isToolActive(pathname: string, tool: Tool) {
  return pathname === tool.href || pathname.startsWith(`${tool.href}/`)
}

function getActiveCategory(pathname: string): ToolCategory | undefined {
  const activeTool = tools.find((tool) => isToolActive(pathname, tool))
  if (activeTool) return activeTool.category

  return categories.find(
    (category) => pathname === `/tools/category/${category.id}`
  )?.id
}

interface NavToolRowProps {
  tool: Tool
  pathname: string
  onNavigate?: () => void
}

function NavToolRow({ tool, pathname, onNavigate }: NavToolRowProps) {
  const { isFavorite, toggleFavorite } = useTools()
  const active = isToolActive(pathname, tool)
  const favorite = isFavorite(tool.id)

  return (
    <div
      className={cn(
        "group relative flex items-center rounded-md transition-colors",
        active ? "bg-primary text-primary-foreground" : "hover:bg-muted"
      )}
    >
      <Link
        href={tool.href}
        onClick={onNavigate}
        aria-current={active ? "page" : undefined}
        className="flex min-w-0 flex-1 items-center gap-2 py-1.5 pl-3 pr-2 text-sm"
      >
        <tool.icon className="h-4 w-4 flex-shrink-0" />
        <span className="min-w-0 flex-1 truncate">{tool.title}</span>
        {tool.isNew && !favorite && (
          <Sparkles
            aria-hidden="true"
            className={cn(
              "h-3 w-3 flex-shrink-0 transition-opacity md:group-hover:opacity-0",
              active ? "text-primary-foreground" : "text-primary"
            )}
          />
        )}
      </Link>
      <button
        type="button"
        aria-label={
          favorite
            ? `Unpin ${tool.title}`
            : `Pin ${tool.title}`
        }
        aria-pressed={favorite}
        title={favorite ? "Unpin from top" : "Pin to top"}
        onClick={() => toggleFavorite(tool.id)}
        className={cn(
          "absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md transition-opacity",
          favorite
            ? "opacity-100"
            : "opacity-70 md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100",
          active
            ? "text-primary-foreground/80 hover:bg-primary-foreground/15 hover:text-primary-foreground"
            : "text-muted-foreground/70 hover:bg-foreground/10 hover:text-foreground"
        )}
      >
        <Star
          aria-hidden="true"
          className={cn(
            "h-3.5 w-3.5 transition-transform",
            favorite ? "fill-yellow-500 text-yellow-500" : "hover:scale-110"
          )}
        />
      </button>
    </div>
  )
}

interface NavGroupProps {
  id: string
  title: string
  icon: React.ReactNode
  tools: Tool[]
  isOpen: boolean
  onToggle: () => void
  pathname: string
  onNavigate?: () => void
}

function NavGroup({
  id,
  title,
  icon,
  tools: categoryTools,
  isOpen,
  onToggle,
  pathname,
  onNavigate,
}: NavGroupProps) {
  return (
    <section className="mb-3">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={id}
        className="mb-1 flex w-full items-center justify-between rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
      >
        <span className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
          <Badge variant="secondary" className="h-4 px-1.5 py-0 text-xs">
            {categoryTools.length}
          </Badge>
        </span>
        <ChevronRight
          aria-hidden="true"
          className={cn("h-4 w-4 transition-transform", isOpen && "rotate-90")}
        />
      </button>
      {isOpen && (
        <div id={id} className="mt-1 space-y-0.5 pl-2">
          {categoryTools.map((tool) => (
            <NavToolRow
              key={tool.id}
              tool={tool}
              pathname={pathname}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </section>
  )
}

interface ShortcutSectionProps {
  id: string
  title: string
  icon: React.ReactNode
  tools: Tool[]
  pathname: string
  onNavigate?: () => void
}

function ShortcutSection({
  id,
  title,
  icon,
  tools: shortcutTools,
  pathname,
  onNavigate,
}: ShortcutSectionProps) {
  if (shortcutTools.length === 0) return null

  return (
    <section className="mb-3" aria-labelledby={id}>
      <div
        id={id}
        className="flex items-center gap-2 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
      >
        {icon}
        <span>{title}</span>
      </div>
      <div className="space-y-0.5 pl-2">
        {shortcutTools.map((tool) => (
          <NavToolRow
            key={tool.id}
            tool={tool}
            pathname={pathname}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </section>
  )
}

function SearchButton() {
  return (
    <button
      type="button"
      onClick={openCommandPalette}
      aria-label="Search tools"
      className="flex w-full items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted"
    >
      <Search className="h-4 w-4" />
      <span className="flex-1 text-left">Search...</span>
      <kbd className="hidden h-5 items-center gap-0.5 rounded border bg-background px-1.5 font-mono text-[10px] text-muted-foreground sm:inline-flex">
        ⌘/Ctrl K
      </kbd>
    </button>
  )
}

interface CollapsedLinkProps {
  href: string
  label: string
  icon: React.ReactNode
  active: boolean
  current?: boolean
  onNavigate?: () => void
}

function CollapsedLink({
  href,
  label,
  icon,
  active,
  current = active,
  onNavigate,
}: CollapsedLinkProps) {
  return (
    <Link
      href={href}
      onClick={onNavigate}
      aria-label={label}
      aria-current={current ? "page" : undefined}
      title={label}
      className={cn(
        "flex h-10 w-10 items-center justify-center rounded-md transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      {icon}
    </Link>
  )
}

interface CollapsedNavigationProps {
  pinnedTools: Tool[]
  recentTools: Tool[]
  pathname: string
  activeCategory?: ToolCategory
}

function CollapsedNavigation({
  pinnedTools,
  recentTools,
  pathname,
  activeCategory,
}: CollapsedNavigationProps) {
  return (
    <div className="flex flex-col items-center gap-1 py-2">
      {pinnedTools.length > 0 && (
        <div
          className="flex flex-col gap-1 border-b pb-2"
          role="group"
          aria-label="Pinned tools"
        >
          {pinnedTools.map((tool) => (
            <CollapsedLink
              key={tool.id}
              href={tool.href}
              label={tool.title}
              icon={<tool.icon className="h-4 w-4" />}
              active={isToolActive(pathname, tool)}
            />
          ))}
        </div>
      )}
      {recentTools.length > 0 && (
        <div
          className="flex flex-col gap-1 border-b py-2"
          role="group"
          aria-label="Recent tools"
        >
          {recentTools.map((tool) => (
            <CollapsedLink
              key={tool.id}
              href={tool.href}
              label={tool.title}
              icon={<tool.icon className="h-4 w-4" />}
              active={isToolActive(pathname, tool)}
            />
          ))}
        </div>
      )}
      <div
        className="flex flex-col gap-1 pt-1"
        role="group"
        aria-label="Tool categories"
      >
        {categories.map((category) => {
          const CategoryIcon = category.icon
          const href = `/tools/category/${category.id}`
          return (
            <CollapsedLink
              key={category.id}
              href={href}
              label={category.title}
              icon={<CategoryIcon className="h-4 w-4" />}
              active={activeCategory === category.id}
              current={pathname === href}
            />
          )
        })}
      </div>
    </div>
  )
}

interface SidebarContentProps {
  idPrefix: string
  collapsed?: boolean
  onNavigate?: () => void
  onToggleCollapsed?: () => void
  openGroups: Record<string, boolean>
  onToggleGroup: (groupId: string, isOpen: boolean) => void
}

function SidebarContent({
  idPrefix,
  collapsed = false,
  onNavigate,
  onToggleCollapsed,
  openGroups,
  onToggleGroup,
}: SidebarContentProps) {
  const pathname = usePathname()
  const { favorites, recent } = useTools()
  const activeCategory = getActiveCategory(pathname)

  const pinnedTools = favorites
    .map((toolId) => getToolById(toolId))
    .filter((tool): tool is Tool => tool !== undefined)
    .slice(0, MAX_PINNED_TOOLS)

  const recentTools = recent
    .filter((toolId) => !favorites.includes(toolId))
    .map((toolId) => getToolById(toolId))
    .filter((tool): tool is Tool => tool !== undefined)
    .slice(0, MAX_RECENT_TOOLS)

  return (
    <div className="flex h-full flex-col">
      <div
        className={cn(
          "flex h-14 flex-shrink-0 items-center border-b",
          collapsed ? "justify-center px-2" : "justify-between px-3"
        )}
      >
        <Link
          href="/"
          onClick={onNavigate}
          aria-label="AKIT home"
          title={collapsed ? "AKIT home" : undefined}
          className="flex items-center gap-2"
        >
          <Logo size="md" markOnly={collapsed} />
        </Link>
        {!collapsed && (
          <div className="flex items-center gap-1">
            <ThemeToggle />
            {onToggleCollapsed && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={onToggleCollapsed}
                aria-label="Collapse sidebar"
                aria-expanded="true"
                aria-keyshortcuts="Meta+B Control+B"
                title="Collapse sidebar (⌘/Ctrl+B)"
                className="h-9 w-9"
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {collapsed ? (
        <div className="flex flex-col items-center gap-1 border-b p-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={openCommandPalette}
            aria-label="Search tools"
            title="Search tools (⌘/Ctrl+K)"
            className="h-10 w-10"
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onToggleCollapsed}
            aria-label="Expand sidebar"
            aria-expanded="false"
            aria-keyshortcuts="Meta+B Control+B"
            title="Expand sidebar (⌘/Ctrl+B)"
            className="h-10 w-10"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex-shrink-0 border-b p-3">
          <SearchButton />
        </div>
      )}

      <nav aria-label="Tool navigation" className="flex min-h-0 flex-1 flex-col">
        <ScrollArea className="flex-1">
          <div className={collapsed ? "px-2" : "px-3 py-3"}>
          {collapsed ? (
            <CollapsedNavigation
              pinnedTools={pinnedTools}
              recentTools={recentTools}
              pathname={pathname}
              activeCategory={activeCategory}
            />
          ) : (
            <>
              <ShortcutSection
                id={`${idPrefix}-pinned-tools`}
                title="Pinned"
                icon={<Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />}
                tools={pinnedTools}
                pathname={pathname}
                onNavigate={onNavigate}
              />
              <ShortcutSection
                id={`${idPrefix}-recent-tools`}
                title="Recent"
                icon={<Clock className="h-3.5 w-3.5" />}
                tools={recentTools}
                pathname={pathname}
                onNavigate={onNavigate}
              />
              {categories.map((category) => {
                const CategoryIcon = category.icon
                return (
                  <NavGroup
                    key={category.id}
                    id={`${idPrefix}-${category.id}-tools`}
                    title={category.title}
                    icon={<CategoryIcon className="h-4 w-4" />}
                    tools={getToolsByCategory(category.id)}
                    isOpen={
                      openGroups[category.id] ?? activeCategory === category.id
                    }
                    onToggle={() =>
                      onToggleGroup(
                        category.id,
                        openGroups[category.id] ?? activeCategory === category.id
                      )
                    }
                    pathname={pathname}
                    onNavigate={onNavigate}
                  />
                )
              })}
            </>
          )}
          </div>
        </ScrollArea>
      </nav>

      {collapsed ? (
        <div className="flex flex-shrink-0 flex-col items-center gap-1 border-t p-2">
          <ThemeToggle />
          <Link
            href="/poker"
            aria-label="Play poker"
            title="Play poker"
            className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
          >
            <Spade className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <div className="flex-shrink-0 border-t p-3 text-center text-xs text-muted-foreground">
          <span>{tools.length} tools available</span>
          <Link
            href="/poker"
            onClick={onNavigate}
            className="group mt-1.5 flex items-center justify-center gap-1.5 text-muted-foreground/50 transition-colors hover:text-primary"
          >
            <Spade className="h-3 w-3 transition-transform group-hover:scale-110" />
            <span>Need a break? Play poker</span>
          </Link>
        </div>
      )}
    </div>
  )
}

export function SidebarNav() {
  const pathname = usePathname()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
  const [persistedStateLoaded, setPersistedStateLoaded] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setMobileOpen(false))
    return () => cancelAnimationFrame(frame)
  }, [pathname])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setCollapsed(localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true")

      const storedGroups = localStorage.getItem(SIDEBAR_GROUPS_KEY)
      if (storedGroups) {
        try {
          const parsedGroups = JSON.parse(storedGroups) as Record<string, unknown>
          const restoredGroups: Record<string, boolean> = {}

          categories.forEach((category) => {
            if (typeof parsedGroups[category.id] === "boolean") {
              restoredGroups[category.id] = parsedGroups[category.id] as boolean
            }
          })

          setOpenGroups(restoredGroups)
        } catch {
          localStorage.removeItem(SIDEBAR_GROUPS_KEY)
        }
      }

      setPersistedStateLoaded(true)
    })

    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    if (!persistedStateLoaded) return
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed))
    localStorage.setItem(SIDEBAR_GROUPS_KEY, JSON.stringify(openGroups))
  }, [collapsed, openGroups, persistedStateLoaded])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => !current)
  }, [])

  const toggleGroup = useCallback((groupId: string, isOpen: boolean) => {
    setOpenGroups((currentGroups) => ({
      ...currentGroups,
      [groupId]: !isOpen,
    }))
  }, [])

  useEffect(() => {
    const handleSidebarShortcut = (event: KeyboardEvent) => {
      if (
        event.key.toLowerCase() === "b" &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey
      ) {
        event.preventDefault()
        toggleCollapsed()
      }
    }

    document.addEventListener("keydown", handleSidebarShortcut)
    return () => document.removeEventListener("keydown", handleSidebarShortcut)
  }, [toggleCollapsed])

  if (pathname.startsWith("/poker")) return null

  return (
    <>
      <div className="sticky top-0 z-50 flex h-14 items-center border-b bg-background/95 px-4 pt-[env(safe-area-inset-top)] backdrop-blur supports-[backdrop-filter]:bg-background/60 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="mr-2">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Open tool navigation</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-[280px] p-0">
            <SheetTitle className="sr-only">Tool navigation</SheetTitle>
            <SidebarContent
              idPrefix="mobile"
              onNavigate={() => setMobileOpen(false)}
              openGroups={openGroups}
              onToggleGroup={toggleGroup}
            />
          </SheetContent>
        </Sheet>
        <Link href="/" aria-label="AKIT home" className="flex items-center gap-2">
          <Logo size="md" />
        </Link>
        <div className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={openCommandPalette}
          aria-label="Search tools"
          title="Search tools"
          className="h-9 w-9"
        >
          <Search className="h-4 w-4" />
        </Button>
        <ThemeToggle />
      </div>

      <aside
        className={cn(
          "sticky top-0 hidden h-screen flex-shrink-0 overflow-hidden border-r bg-background transition-[width] duration-200 md:block",
          collapsed ? "w-16" : "w-72"
        )}
      >
        <SidebarContent
          idPrefix="desktop"
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
        />
      </aside>
    </>
  )
}
